import { Directive, ElementRef, Renderer2 } from '@angular/core';
import { ModalController } from '@ionic/angular';

/**
 * Problem: After opening a modal and either pressing tabs on keyboard, or navigating using voice over through phone accessibility tools, the navigation goes over the elements in the underlying parent page, and not in the active modal.
 *
 * Solution: Hide previous page/modal/alerts from the screen reader by adding aria-hidden to the previous element using mutation observer on ion-app.
 *
 */

@Directive({
  selector: 'ion-app',
})
export class ModalAccessibilityDirective {
  private changes: MutationObserver;

  constructor(
    private elementRef: ElementRef,
    protected modalController: ModalController,
    private renderer: Renderer2,
  ) {
    const element = this.elementRef.nativeElement;

    // https://levelup.gitconnected.com/the-mutationobserver-web-api-cf469261eb36
    this.changes = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        this.setStackedElementsVisibility(mutation);
      });
    });

    // Mutation observer is fired when the child count of target element(ion-app) is updated.
    this.changes.observe(element, {
      childList: true,
    });
  }

  /**
   * This function hides all stacked elements from screen reader except for the last one.
   * The goal is for the screen reader to read only the top element in full page read mode.
   *
   * For now we are considering all element nodes but later can add checks for specific elements like modals, alerts, and loaders only.
   *
   * @param mutation - Mutation object returned by the observer on target element (ion-app) change.
   */
  setStackedElementsVisibility(mutation: MutationRecord) {
    const ionAppElement = <HTMLElement>mutation.target;
    const childCount = ionAppElement.childElementCount;
    if (childCount) {
      if (childCount === 1) {
        // When ion-router-outlet is the only child.
        this.renderer.removeAttribute(ionAppElement.children[0], 'aria-hidden');
      } else {
        // Get previous element sibling of type element. This is to exclude nodes of type comments and other framework related nodes.
        const previouSibling =
          mutation.previousSibling.nodeType === 1
            ? mutation.previousSibling
            : (<HTMLElement>mutation.previousSibling).previousElementSibling;

        if (mutation.addedNodes.length) {
          // Add aria-hidden to the last active element (before addition of new nodes).
          this.renderer.setAttribute(previouSibling, 'aria-hidden', 'true');

          // Add aria-hidden to added elements except for the last one
          for (let i = 0; i < mutation.addedNodes.length - 1; i += 1) {
            this.renderer.setAttribute(mutation.addedNodes[i], 'aria-hidden', 'true');
          }
        } else if (mutation.removedNodes.length) {
          // Remove aria-hidden from the next/top element in the stack when the last child is being removed.
          this.renderer.removeAttribute(previouSibling, 'aria-hidden');
        }
      }
    }
  }
}
